import { Text } from "@/components/podkit/typography/Text";
import { SkeletonBlock, SkeletonText } from "@/components/podkit/loading/Skeleton";
import { RunnerDetailsSection, type SectionStatus } from "@/components/runners/details/RunnerDetailsSection";
import { Switch } from "@/components/Switch";
import { Input } from "@/components/podkit/forms/Input";
import { PasswordInput } from "@/components/podkit/forms/PasswordInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { Button } from "@/components/flexkit/Button";
import { useUpdateRunner } from "@/queries/runner-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import { type Runner, LogLevel } from "gitpod-next-api/gitpod/v1/runner_pb";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb";
import React, { useCallback, useState } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { useMembership } from "@/hooks/use-membership";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { useFlagGroupManagementEnabled } from "@/hooks/use-flag";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";

type RunnerConfigurationProps = {
    runner: PlainMessage<Runner>;
    hasSignedUp: boolean;
};

export const RunnerConfiguration: React.FC<RunnerConfigurationProps> = ({ runner, hasSignedUp }) => {
    const updateRunner = useUpdateRunner();
    const { membership } = useMembership();
    const { value: isGroupManagementEnabled } = useFlagGroupManagementEnabled();
    const { hasPermission } = useResourcePermissions({ enabled: isGroupManagementEnabled });

    // Local state for metrics configuration - sync with runner prop
    const [metricsEnabled, setMetricsEnabled] = useState(runner.spec?.configuration?.metrics?.enabled ?? false);
    const [metricsUrl, setMetricsUrl] = useState(runner.spec?.configuration?.metrics?.url ?? "");

    // Credentials are write-only for security - never display existing values
    const [metricsUsername, setMetricsUsername] = useState("");
    const [metricsPassword, setMetricsPassword] = useState("");

    // Individual loading states
    const [devContainerLoading, setDevContainerLoading] = useState(false);
    const [metricsToggleLoading, setMetricsToggleLoading] = useState(false);
    const [metricsConfigLoading, setMetricsConfigLoading] = useState(false);
    const [logLevelLoading, setLogLevelLoading] = useState(false);

    // Sync only non-sensitive state with runner prop when it changes
    React.useEffect(() => {
        setMetricsEnabled(runner.spec?.configuration?.metrics?.enabled ?? false);
        setMetricsUrl(runner.spec?.configuration?.metrics?.url ?? "");
        // Never sync credentials - they remain empty for security
    }, [runner.spec?.configuration?.metrics]);

    // Helper function to get log level display text
    const getLogLevelText = useCallback((level: LogLevel): string => {
        switch (level) {
            case LogLevel.DEBUG:
                return "Debug";
            case LogLevel.INFO:
                return "Info";
            case LogLevel.WARN:
                return "Warn";
            case LogLevel.ERROR:
                return "Error";
            default:
                return "Info"; // fallback
        }
    }, []);

    const currentLogLevel = runner.spec?.configuration?.logLevel ?? LogLevel.INFO;

    const handleDevcontainerImageCacheToggle = useCallback(
        async (checked: boolean) => {
            setDevContainerLoading(true);
            try {
                await updateRunner.mutateAsync({
                    runnerId: runner.runnerId,
                    spec: {
                        configuration: {
                            devcontainerImageCacheEnabled: checked,
                        },
                    },
                });
                toast({
                    title: "Configuration updated",
                    description: checked
                        ? "Dev container image cache is now enabled"
                        : "Dev container image cache is now disabled",
                });
            } catch (error) {
                toast({
                    title: "Failed to update dev container image cache",
                    description: formatError(error),
                });
            } finally {
                setDevContainerLoading(false);
            }
        },
        [runner.runnerId, updateRunner],
    );

    const handleMetricsToggle = useCallback(
        async (checked: boolean) => {
            setMetricsToggleLoading(true);
            try {
                setMetricsEnabled(checked);

                // Build metrics config - only include credentials if they have values
                const metricsConfig: Record<string, string | boolean> = {
                    enabled: checked,
                    url: metricsUrl,
                };

                // Only include credentials if they have been entered
                if (metricsUsername) {
                    metricsConfig.username = metricsUsername;
                }
                if (metricsPassword) {
                    metricsConfig.password = metricsPassword;
                }

                await updateRunner.mutateAsync({
                    runnerId: runner.runnerId,
                    spec: {
                        configuration: {
                            metrics: metricsConfig,
                        },
                    },
                });
                toast({
                    title: "Configuration updated",
                    description: checked ? "Metrics collection is now enabled" : "Metrics collection is now disabled",
                });
            } catch (error) {
                setMetricsEnabled(!checked);
                toast({
                    title: "Failed to update metrics configuration",
                    description: formatError(error),
                });
            } finally {
                setMetricsToggleLoading(false);
            }
        },
        [runner.runnerId, updateRunner, metricsUrl, metricsUsername, metricsPassword],
    );

    const handleSaveMetricsConfig = useCallback(async () => {
        setMetricsConfigLoading(true);
        try {
            // Build metrics config - only include non-empty values
            const metricsConfig: Record<string, string | boolean> = {
                enabled: metricsEnabled,
                url: metricsUrl,
            };

            // Only include credentials if they have values
            if (metricsUsername.trim()) {
                metricsConfig.username = metricsUsername;
            }
            if (metricsPassword.trim()) {
                metricsConfig.password = metricsPassword;
            }

            await updateRunner.mutateAsync({
                runnerId: runner.runnerId,
                spec: {
                    configuration: {
                        metrics: metricsConfig,
                    },
                },
            });

            // Clear credential fields after successful submission for security
            setMetricsUsername("");
            setMetricsPassword("");

            toast({
                title: "Configuration updated",
                description: "Metrics configuration saved successfully",
            });
        } catch (error) {
            toast({
                title: "Failed to update metrics configuration",
                description: formatError(error),
            });
        } finally {
            setMetricsConfigLoading(false);
        }
    }, [runner.runnerId, updateRunner, metricsEnabled, metricsUrl, metricsUsername, metricsPassword]);

    // Check if there are unsaved changes to show save button
    const hasUnsavedMetricsChanges = Boolean(
        metricsUrl !== (runner.spec?.configuration?.metrics?.url ?? "") ||
            metricsUsername.trim() ||
            metricsPassword.trim(),
    );

    const handleLogLevelChange = useCallback(
        async (logLevel: string) => {
            setLogLevelLoading(true);
            try {
                const level = parseInt(logLevel) as LogLevel;
                await updateRunner.mutateAsync({
                    runnerId: runner.runnerId,
                    spec: {
                        configuration: {
                            logLevel: level,
                        },
                    },
                });
                toast({
                    title: "Configuration updated",
                    description: `Log level updated to ${LogLevel[level].toLowerCase()}`,
                });
            } catch (error) {
                toast({
                    title: "Failed to update log level",
                    description: formatError(error),
                });
            } finally {
                setLogLevelLoading(false);
            }
        },
        [runner.runnerId, updateRunner],
    );

    const supportsConfiguration = runner.provider === RunnerProvider.AWS_EC2 || runner.provider === RunnerProvider.GCP;
    if (!supportsConfiguration) {
        return null;
    }

    let status: SectionStatus = "optional";
    if (!hasSignedUp) {
        status = "locked";
    }

    // Fallback to admin check when feature flag is disabled
    const isAdmin = membership?.userRole === OrganizationRole.ADMIN;
    const canUpdateRunner = isGroupManagementEnabled
        ? hasPermission(ResourceType.RUNNER, runner.runnerId, "runner:update")
        : isAdmin;
    const readOnly = status === "locked" || !canUpdateRunner;

    return (
        <RunnerDetailsSection title="Runner Configuration" status={status} hideStatus={!hasSignedUp}>
            <div className="flex flex-col gap-4" data-testid="runner-configuration-section">
                <div className="flex flex-col gap-1">
                    <SkeletonText ready={true} size="lg" className="w-48">
                        <Text className={cn(status === "locked" && "text-content-secondary", "text-base")}>
                            Configure runner settings and behavior
                        </Text>
                    </SkeletonText>
                </div>
                <SkeletonBlock ready={true} failed={false} className="mt-1">
                    <div className="space-y-6">
                        {/* Dev container image cache */}
                        <div className="flex items-center gap-3">
                            <Switch
                                state={
                                    devContainerLoading
                                        ? "pending"
                                        : runner.spec?.configuration?.devcontainerImageCacheEnabled
                                          ? "checked"
                                          : "unchecked"
                                }
                                onToggle={(checked) => Promise.resolve(handleDevcontainerImageCacheToggle(checked))}
                                disabled={readOnly}
                                id="devcontainer-image-cache-toggle"
                            />
                            <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                    <Text className="text-base">Dev container image cache</Text>
                                    <ExternalLink
                                        href="https://ona.com/docs/ona/runners/aws/devcontainer-image-cache"
                                        iconSize="sm"
                                    >
                                        Learn more
                                    </ExternalLink>
                                </div>
                                <Text className="text-sm text-content-secondary">
                                    Reduce environment startup time by using a shared project-wide build cache for dev
                                    container images
                                </Text>
                            </div>
                        </div>

                        {/* Metrics configuration */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <Switch
                                    state={metricsToggleLoading ? "pending" : metricsEnabled ? "checked" : "unchecked"}
                                    onToggle={(checked) => Promise.resolve(handleMetricsToggle(checked))}
                                    disabled={readOnly}
                                    id="metrics-toggle"
                                />
                                <div className="flex flex-col">
                                    <Text className="text-base">Enable metrics collection</Text>
                                    <Text className="text-sm text-content-secondary">
                                        Collect and send runner metrics to an external metrics collector
                                    </Text>
                                </div>
                            </div>

                            {metricsEnabled && (
                                <form
                                    className="ml-[52px] space-y-3"
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        void handleSaveMetricsConfig();
                                    }}
                                >
                                    <div className="flex flex-col gap-1">
                                        <label htmlFor="metrics-url-input" className="text-sm font-medium">
                                            Metrics collector URL
                                        </label>
                                        <Input
                                            id="metrics-url-input"
                                            value={metricsUrl}
                                            onChange={(e) => setMetricsUrl(e.target.value)}
                                            placeholder="https://metrics.example.com"
                                            disabled={readOnly}
                                            data-testid="metrics-url-input"
                                            type="text"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label htmlFor="metrics-username-input" className="text-sm font-medium">
                                            New username
                                        </label>
                                        <Input
                                            id="metrics-username-input"
                                            value={metricsUsername}
                                            onChange={(e) => setMetricsUsername(e.target.value)}
                                            placeholder="Optional - leave empty to keep existing username"
                                            disabled={readOnly}
                                            type="text"
                                            data-testid="metrics-username-input"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <label htmlFor="metrics-password-input" className="text-sm font-medium">
                                            New password
                                        </label>
                                        <PasswordInput
                                            id="metrics-password-input"
                                            value={metricsPassword}
                                            onChange={(e) => setMetricsPassword(e.target.value)}
                                            placeholder="Optional - leave empty to keep existing password"
                                            disabled={readOnly}
                                            data-testid="metrics-password-input"
                                            autoComplete="off"
                                            revealable={true}
                                        />
                                    </div>

                                    <div className="pt-2">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            size="sm"
                                            disabled={readOnly || metricsConfigLoading || !hasUnsavedMetricsChanges}
                                            loading={metricsConfigLoading}
                                            data-testid="save-metrics-button"
                                            data-tracking-id="save-runner-configuration-runner-configuration"
                                        >
                                            Save Configuration
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </div>

                        {/* Log level configuration */}
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-col">
                                <Text className="text-base">Log level</Text>
                                <Text className="text-sm text-content-secondary">
                                    Set the logging verbosity for the runner
                                </Text>
                            </div>
                            <div className="ml-0">
                                <Select
                                    value={String(currentLogLevel)}
                                    onValueChange={handleLogLevelChange}
                                    disabled={readOnly}
                                >
                                    <SelectTrigger
                                        loading={logLevelLoading}
                                        className="w-48"
                                        data-testid="log-level-select"
                                    >
                                        <SelectValue>{getLogLevelText(currentLogLevel)}</SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={LogLevel.DEBUG.toString()}>
                                            <Text>Debug</Text>
                                        </SelectItem>
                                        <SelectItem value={LogLevel.INFO.toString()}>
                                            <Text>Info</Text>
                                        </SelectItem>
                                        <SelectItem value={LogLevel.WARN.toString()}>
                                            <Text>Warn</Text>
                                        </SelectItem>
                                        <SelectItem value={LogLevel.ERROR.toString()}>
                                            <Text>Error</Text>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                </SkeletonBlock>
            </div>
        </RunnerDetailsSection>
    );
};
